import { useCallback, useEffect, useMemo, useState } from "react";

import dayjs from "dayjs";

import { Search as SearchIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  DateRangePicker,
  SingleInputDateRangeField,
} from "@mui/x-date-pickers-pro";
import { PDFViewer } from "@react-pdf/renderer";
import { PageContainer } from "@/features/ui";

import SearchSvg from "../assets/search.svg";
import { useAuth } from "@/features/authentication";
import { OutletRoleEnum, UserOutletsSelect } from "@/features/outlets";
import { SalesOrderProductVariantHubPdf } from "@/features/reports";
import { apiOutletClient } from "@/features/shared";
import { CenteredLoading } from "@/features/ui";

export const SalesOrderProductVariantHubReport = () => {
  const { user, outlet, changeOutlet } = useAuth();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));

  const isExactlyMd = isMdUp && !isLgUp;

  const [dateRange, setDateRange] = useState([dayjs(), dayjs()]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState();

  // Enforce a hub outlet selection: if the globally-selected outlet is not
  // a hub (e.g., user came from the retail report page), switch to the first
  // hub outlet the user has access to.
  useEffect(() => {
    if (outlet && outlet.role !== OutletRoleEnum.HUB) {
      const firstHubOutlet = user?.outlets?.find(
        (o) => o.role === OutletRoleEnum.HUB,
      );
      if (firstHubOutlet) changeOutlet(firstHubOutlet);
    }
  }, [outlet, user, changeOutlet]);

  // Function to fetch data from the API
  const fetchData = useCallback(async () => {
    const [startDate, endDate] = dateRange;
    if (!startDate || !endDate) return;

    setData(null);
    setLoading(true);
    try {
      const {
        data: { data },
      } = await apiOutletClient.get(
        "sales-order-products/report-by-buyer-outlet",
        {
          params: {
            startDate: startDate.format("YYYY-MM-DD"),
            endDate: endDate.format("YYYY-MM-DD"),
          },
        },
      );

      setData(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  const pdfDocument = useMemo(() => {
    if (!data) return null;

    return (
      <PDFViewer width="100%" height="700">
        <SalesOrderProductVariantHubPdf
          user={user}
          outlet={outlet}
          data={data}
          dateRange={dateRange}
        />
      </PDFViewer>
    );
  }, [data, user, outlet, dateRange]);

  return (
    <PageContainer maxWidth="xl" breadcrumbs={[]}>
      <Stack spacing={3}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 5, md: 4, lg: 5 }}>
            <UserOutletsSelect
              value={outlet}
              onChange={changeOutlet}
              allowedRoles={[OutletRoleEnum.HUB]}
            />
          </Grid>
          <Grid size={{ xs: 7, md: 6, lg: 5 }}>
            <DateRangePicker
              value={dateRange}
              disableFuture
              slots={{
                field: SingleInputDateRangeField,
              }}
              slotProps={{
                field: {
                  fullWidth: true,
                },
              }}
              format="DD/MMM/YYYY"
              localeText={{ start: "Dari", end: "Sampai" }}
              onChange={(newDateRange) => {
                setDateRange(newDateRange);
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2, lg: 2 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={!isExactlyMd && <SearchIcon />}
              onClick={fetchData}
            >
              {isExactlyMd && <SearchIcon />}
              {!isExactlyMd && "Cari"}
            </Button>
          </Grid>
        </Grid>
        {!loading && !data && (
          <Stack
            sx={{ justifyContent: "center", alignItems: "center", pt: 10 }}
            spacing={2}
          >
            <Box component="img" src={SearchSvg} sx={{
              height: "20vh"
            }} />
            <Typography sx={{ fontSize: "1.5rem" }}>
              Lakukan pencarian
            </Typography>
          </Stack>
        )}
        {loading && <CenteredLoading sx={{ height: "80vh" }} />}
        {data && pdfDocument}
      </Stack>
    </PageContainer>
  );
};

export default SalesOrderProductVariantHubReport;
